import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, Package, Plus, Trash2, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatRupeeFixed, formatRupeeInt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorOrders = () => {
  const { user } = useOutletContext();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [actionNeeded, setActionNeeded] = useState(false);
  const [vendorQtyDialog, setVendorQtyDialog] = useState({ open: false, itemId: '', status: '', qty: 0, ordered: 0, dispatchDate: new Date().toISOString().split('T')[0] });
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [payReqDialog, setPayReqDialog] = useState(false);
  const [payReqForm, setPayReqForm] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });

  const ACTION_STATUSES = ['Pending', 'Ready for Delivery', 'Ordered from Vendor'];

  useEffect(() => {
    fetchVendorItems();
    fetchPaymentRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [items, productFilter, statusFilter, typeFilter, actionNeeded]);

  const fetchVendorItems = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendor/order-items`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load order items');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/vendor/payment-requests`, { withCredentials: true });
      setPaymentRequests(res.data);
    } catch (error) { /* ignore */ }
  };

  const submitPaymentRequest = async (e) => {
    e.preventDefault();
    if (!payReqForm.amount || !payReqForm.description) { toast.error('Amount and description are required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/vendor/payment-requests`, { ...payReqForm, amount: parseFloat(payReqForm.amount) }, { withCredentials: true });
      toast.success('Payment request submitted');
      setPayReqDialog(false);
      setPayReqForm({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchPaymentRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const cancelPaymentRequest = async (requestId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/vendor/payment-requests/${requestId}`, { status: 'Cancelled' }, { withCredentials: true });
      toast.success('Request cancelled');
      fetchPaymentRequests();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const applyFilters = () => {
    let filtered = [...items];

    if (productFilter) {
      const keywords = productFilter.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter(item => {
        const str = `${item.product_name} ${item.product_brand} ${item.product_size} ${item.product_color}`.toLowerCase();
        return keywords.every(kw => str.includes(kw));
      });
    }

    if (statusFilter && statusFilter !== 'ALL') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (typeFilter && typeFilter !== 'ALL') {
      filtered = filtered.filter(item => item.order_type === typeFilter);
    }

    if (actionNeeded) {
      filtered = filtered.filter(item => ACTION_STATUSES.includes(item.status));
    }

    setFilteredItems(filtered);
  };

  const handleStatusChange = async (orderItemId, newStatus, item) => {
    if (newStatus === 'Dispatched from Vendor') {
      setVendorQtyDialog({ open: true, itemId: orderItemId, status: newStatus, qty: item.quantity, ordered: item.quantity, dispatchDate: new Date().toISOString().split('T')[0] });
      return;
    }
    try {
      await axios.patch(
        `${BACKEND_URL}/api/order-items/${orderItemId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchVendorItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const confirmVendorQtyStatus = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/order-items/${vendorQtyDialog.itemId}/status`,
        { status: vendorQtyDialog.status, actual_qty: vendorQtyDialog.qty, dispatch_date: vendorQtyDialog.dispatchDate },
        { withCredentials: true }
      );
      toast.success(`Status updated to ${vendorQtyDialog.status}`);
      fetchVendorItems();
      setVendorQtyDialog({ open: false, itemId: '', status: '', qty: 0, ordered: 0, dispatchDate: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const [editingQty, setEditingQty] = useState({ itemId: null, field: null, value: '' });

  const handleQtyEdit = async (itemId, field, newValue) => {
    const val = parseFloat(newValue);
    if (isNaN(val) || val < 0) { toast.error('Invalid quantity'); return; }
    try {
      await axios.patch(`${BACKEND_URL}/api/order-items/${itemId}/edit-qty`, { field, value: val }, { withCredentials: true });
      toast.success('Quantity updated');
      setEditingQty({ itemId: null, field: null, value: '' });
      fetchVendorItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-stone-100 text-stone-800',
      'Delivered': 'bg-lime-100 text-lime-800',
      'No Stock': 'bg-red-100 text-red-800',
      'Ordered from Vendor': 'bg-blue-100 text-blue-800',
      'Ready for Delivery': 'bg-amber-100 text-amber-800',
      'Dispatched from Vendor': 'bg-amber-100 text-amber-800',
      'Received in Store': 'bg-lime-100 text-lime-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-stone-100 text-stone-800'}`}>
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'Purchase Order') {
      return <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">Restock</span>;
    }
    return <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-stone-50 text-stone-700 border border-stone-200">Customer</span>;
  };

  // Vendor-allowed statuses — now unified across both types
  const vendorStatuses = ['Pending', 'Ready for Delivery', 'Dispatched from Vendor'];

  const getStatusOptions = () => {
    return vendorStatuses;
  };

  const allStatuses = ['Pending', 'Ready for Delivery', 'Dispatched from Vendor', 'Ordered from Vendor', 'Received in Store', 'Delivered', 'Cancelled'];

  if (loading) {
    return <div className="text-stone-600 text-sm">Loading your order items...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" data-testid="vendor-orders-title" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Orders with Your Products
          </h1>
          <p className="text-xs text-stone-600 mt-1">
            {filteredItems.length} items {productFilter || statusFilter !== 'ALL' || typeFilter !== 'ALL' ? '(filtered)' : 'total'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="vendor-refresh-btn" onClick={() => { fetchVendorItems(); fetchPaymentRequests(); }} variant="outline" size="sm" className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs h-8">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button data-testid="vendor-payment-request-btn" onClick={() => setPayReqDialog(true)} size="sm" className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8">
            <IndianRupee className="w-3 h-3 mr-1" /> Request Payment
          </Button>
        </div>
      </div>

      {/* Payment Requests */}
      {paymentRequests.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-stone-200 bg-stone-50">
            <h3 className="text-xs font-semibold text-stone-700">Your Payment Requests</h3>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-2 font-semibold text-stone-600">DATE</th>
                <th className="text-left p-2 font-semibold text-stone-600">DESCRIPTION</th>
                <th className="text-right p-2 font-semibold text-stone-600">AMOUNT</th>
                <th className="text-left p-2 font-semibold text-stone-600">STATUS</th>
                <th className="text-right p-2 font-semibold text-stone-600 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {paymentRequests.map((pr) => (
                <tr key={pr.request_id} data-testid={`pay-req-${pr.request_id}`} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="p-2 tabular-nums text-stone-700">{pr.date}</td>
                  <td className="p-2 text-stone-800">{pr.description}</td>
                  <td className="p-2 text-right font-semibold tabular-nums text-stone-900">{formatRupeeFixed(pr.amount)}</td>
                  <td className="p-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      pr.status === 'Pending' ? 'bg-amber-100 text-amber-800' :
                      pr.status === 'Approved' ? 'bg-lime-100 text-lime-800' :
                      pr.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                      'bg-stone-100 text-stone-600'
                    }`}>{pr.status}</span>
                  </td>
                  <td className="p-2 text-right">
                    {pr.status === 'Pending' && (
                      <button onClick={() => cancelPaymentRequest(pr.request_id)} className="text-red-500 hover:text-red-700" title="Cancel request">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-md p-3">
        <div className="flex gap-2 items-center">
          <Button
            data-testid="vendor-action-needed-btn"
            onClick={() => setActionNeeded(!actionNeeded)}
            size="sm"
            className={`text-xs h-8 ${actionNeeded ? 'bg-amber-700 hover:bg-amber-800 text-white' : 'bg-white text-stone-700 border border-stone-200 hover:bg-stone-50'}`}
          >
            Action Needed
          </Button>
          <Input
            placeholder="Search product (name, brand, size, color)..."
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="text-xs h-8 border-stone-200"
            data-testid="vendor-filter-product"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="text-xs h-8 border-stone-200" data-testid="vendor-filter-status">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-xs">All Statuses</SelectItem>
              {allStatuses.map(status => (
                <SelectItem key={status} value={status} className="text-xs">{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="text-xs h-8 border-stone-200" data-testid="vendor-filter-type">
              <SelectValue placeholder="Filter by Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-xs">All Types</SelectItem>
              <SelectItem value="Customer Order" className="text-xs">Customer Orders</SelectItem>
              <SelectItem value="Purchase Order" className="text-xs">Purchase Orders (Restock)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      {items.length === 0 ? (
        <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-900 mb-2">No Orders Yet</h3>
          <p className="text-stone-600">Orders containing your products will appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
            <table className="w-full text-xs border-collapse">
              <thead className="bg-stone-100 sticky top-0 z-10">
                <tr className="border-b border-stone-300">
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-20">TYPE</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-48">PRODUCT</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">ORDER ID</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">DATE</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-16">ORD.</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-16">DISP.</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-16">RCVD.</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-24">EXPECTED</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-24">ACCEPTED</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-28">STATUS</th>
                  <th className="p-2 text-left font-semibold text-stone-700 w-44">UPDATE STATUS</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="p-6 text-center text-stone-500 text-xs">
                      No items match your filters.
                    </td>
                  </tr>
                ) : (
                  (() => {
                    // Group items by dispatch_date
                    const groups = {};
                    filteredItems.forEach(item => {
                      const key = item.dispatch_date || 'Not Dispatched';
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(item);
                    });
                    // Sort groups: actual dates descending, 'Not Dispatched' last
                    const sortedKeys = Object.keys(groups).sort((a, b) => {
                      if (a === 'Not Dispatched') return 1;
                      if (b === 'Not Dispatched') return -1;
                      return b.localeCompare(a);
                    });
                    return sortedKeys.map(dateKey => {
                      const groupItems = groups[dateKey];
                      const totalExpected = groupItems.reduce((s, i) => s + (i.dispatched_qty || 0) * (i.input_price || 0), 0);
                      const totalAccepted = groupItems.reduce((s, i) => s + (i.received_qty || 0) * (i.input_price || 0), 0);
                      const hasAnyDispatch = groupItems.some(i => i.dispatched_qty > 0);
                      const hasAnyReceived = groupItems.some(i => i.received_qty > 0);
                      return (
                        <React.Fragment key={dateKey}>
                          {/* Group header */}
                          <tr className="bg-amber-50 border-b border-amber-200">
                            <td colSpan="7" className="p-2 text-xs font-semibold text-amber-900">
                              {dateKey === 'Not Dispatched' ? 'Not Yet Dispatched' : `Dispatch Date: ${dateKey}`}
                              <span className="ml-2 font-normal text-amber-700">({groupItems.length} items)</span>
                            </td>
                            <td className="p-2 text-right text-xs font-bold text-stone-800 tabular-nums">
                              {hasAnyDispatch ? formatRupeeInt(totalExpected) : '-'}
                            </td>
                            <td className="p-2 text-right text-xs font-bold tabular-nums">
                              {hasAnyReceived ? (
                                <span className={totalExpected !== totalAccepted && hasAnyDispatch ? 'text-red-700' : 'text-lime-700'}>
                                  {formatRupeeInt(totalAccepted)}
                                </span>
                              ) : '-'}
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                          {/* Group items */}
                          {groupItems.map((item, index) => (
                    <tr
                      key={`${item.order_type}-${item.order_item_id}`}
                      data-testid={`vendor-item-${item.order_item_id}`}
                      className={`border-b border-stone-200 hover:bg-stone-50 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
                    >
                      <td className="border-r border-stone-200 p-2 w-20">{getTypeBadge(item.order_type)}</td>
                      <td className="border-r border-stone-200 p-2 w-48">
                        <div className="font-medium text-stone-900">
                          {item.product_name}
                          {(!item.mrp || item.mrp <= 0) && <span className="ml-1 inline-block px-1 py-0 rounded text-[10px] font-semibold bg-amber-200 text-amber-900">Update pricing</span>}
                        </div>
                        <div className="text-stone-600">{item.product_brand} {item.product_size && `\u2022 ${item.product_size}`} {item.product_color && `\u2022 ${item.product_color}`}</div>
                      </td>
                      <td className="border-r border-stone-200 p-2 text-stone-600 font-mono w-24">{item.order_id}</td>
                      <td className="border-r border-stone-200 p-2 text-stone-700 tabular-nums w-24">{item.order_date}</td>
                      <td className="border-r border-stone-200 p-2 text-right font-medium text-stone-900 tabular-nums w-16">{item.quantity}</td>
                      <td className="border-r border-stone-200 p-2 text-right tabular-nums w-16">
                        {editingQty.itemId === item.order_item_id && editingQty.field === 'dispatched_qty' ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            autoFocus
                            className="w-14 text-right text-xs border border-blue-300 rounded px-1 py-0.5 bg-blue-50"
                            value={editingQty.value}
                            onChange={(e) => setEditingQty({ ...editingQty, value: e.target.value })}
                            onBlur={() => handleQtyEdit(item.order_item_id, 'dispatched_qty', editingQty.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleQtyEdit(item.order_item_id, 'dispatched_qty', editingQty.value); if (e.key === 'Escape') setEditingQty({ itemId: null, field: null, value: '' }); }}
                          />
                        ) : (
                          <span
                            className={`cursor-pointer hover:bg-blue-50 hover:text-blue-800 px-1 rounded ${item.dispatched_qty ? '' : 'text-stone-400'}`}
                            onClick={() => setEditingQty({ itemId: item.order_item_id, field: 'dispatched_qty', value: item.dispatched_qty || '' })}
                            title="Click to edit"
                          >
                            {item.dispatched_qty || '-'}
                          </span>
                        )}
                      </td>
                      <td className="border-r border-stone-200 p-2 text-right tabular-nums w-16">{item.received_qty || '-'}</td>
                      {(() => {
                        const ip = item.input_price || 0;
                        const expected = (item.dispatched_qty || 0) * ip;
                        const accepted = (item.received_qty || 0) * ip;
                        const hasMismatch = item.dispatched_qty > 0 && item.received_qty > 0 && item.dispatched_qty !== item.received_qty;
                        return (
                          <>
                            <td className="border-r border-stone-200 p-2 text-right tabular-nums w-24">
                              {item.dispatched_qty > 0 ? <span className="text-stone-700">{formatRupeeInt(expected)}</span> : <span className="text-stone-400">-</span>}
                            </td>
                            <td className="border-r border-stone-200 p-2 text-right tabular-nums w-24">
                              {item.received_qty > 0 ? (
                                <span className={hasMismatch ? 'text-red-700 font-semibold' : 'text-lime-700 font-semibold'}>
                                  {formatRupeeInt(accepted)}
                                  {hasMismatch && <span className="text-[10px] ml-0.5 font-normal">(dispute)</span>}
                                </span>
                              ) : <span className="text-stone-400">-</span>}
                            </td>
                          </>
                        );
                      })()}
                      <td className="border-r border-stone-200 p-2 w-28">{getStatusBadge(item.status)}</td>
                      <td className="p-2 w-44">
                        <Select
                          value={item.status}
                          onValueChange={(value) => handleStatusChange(item.order_item_id, value, item)}
                        >
                          <SelectTrigger data-testid={`vendor-status-select-${item.order_item_id}`} className="w-full border-stone-200 text-xs h-7">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {getStatusOptions().map((status) => (
                              <SelectItem key={status} value={status} className="text-xs">
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                          ))}
                        </React.Fragment>
                      );
                    });
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-stone-50 border border-stone-200 rounded-md p-2 flex justify-between items-center text-xs">
        <div className="text-stone-600">
          Total: <span className="font-semibold text-stone-900">{filteredItems.length}</span> order items
        </div>
        <div className="text-stone-600">
          {(productFilter || statusFilter !== 'ALL' || typeFilter !== 'ALL') ? 'Showing filtered results' : 'Showing all items'}
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <h3 className="text-xs font-semibold text-amber-900 mb-1">Vendor Notes:</h3>
        <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
          <li><strong>All Orders:</strong> Update to Pending, Ready for Delivery, or Dispatched from Vendor</li>
          <li>When dispatching, enter the actual quantity being sent</li>
          <li>Final received/delivered status is managed by the store owner</li>
        </ul>
      </div>

      {/* Dispatch quantity dialog */}
      <Dialog open={vendorQtyDialog.open} onOpenChange={(open) => { if (!open) setVendorQtyDialog(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Dispatching Quantity</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Ordered: <span className="font-semibold text-stone-900">{vendorQtyDialog.ordered}</span>
            </p>
            <div>
              <Label>Quantity Being Dispatched</Label>
              <Input
                data-testid="vendor-qty-dialog-input"
                type="number"
                step="0.01"
                min="0"
                value={vendorQtyDialog.qty}
                onChange={(e) => setVendorQtyDialog(prev => ({ ...prev, qty: parseFloat(e.target.value) || 0 }))}
                className="border-stone-200"
                autoFocus
              />
            </div>
            <div>
              <Label>Dispatch Date</Label>
              <Input
                data-testid="vendor-dispatch-date-input"
                type="date"
                value={vendorQtyDialog.dispatchDate}
                onChange={(e) => setVendorQtyDialog(prev => ({ ...prev, dispatchDate: e.target.value }))}
                className="border-stone-200"
              />
            </div>
            <Button data-testid="vendor-qty-dialog-confirm" onClick={confirmVendorQtyStatus} className="w-full bg-amber-700 hover:bg-amber-800 text-white">
              Confirm Dispatch
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Request Dialog */}
      <Dialog open={payReqDialog} onOpenChange={setPayReqDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Request Payment</DialogTitle></DialogHeader>
          <form onSubmit={submitPaymentRequest} className="space-y-4">
            <div>
              <Label>Amount *</Label>
              <Input data-testid="pay-req-amount" type="number" step="0.01" min="0.01" value={payReqForm.amount} onChange={(e) => setPayReqForm({ ...payReqForm, amount: e.target.value })} placeholder="0.00" required className="border-stone-200" />
            </div>
            <div>
              <Label>Description *</Label>
              <Input data-testid="pay-req-desc" value={payReqForm.description} onChange={(e) => setPayReqForm({ ...payReqForm, description: e.target.value })} placeholder="e.g., Transportation for dispatch on 07-Apr" required className="border-stone-200" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={payReqForm.date} onChange={(e) => setPayReqForm({ ...payReqForm, date: e.target.value })} className="border-stone-200" />
            </div>
            <Button type="submit" data-testid="pay-req-submit" className="w-full bg-amber-700 hover:bg-amber-800 text-white">Submit Request</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorOrders;
